import { AVISO_NAO_ACONSELHAMENTO } from '@modules/aawz-intelligence-v2/constants/Text'
import {
  CANAL_CARTEIRA,
  CANAL_TRANSCRICAO,
} from '@modules/aawz-intelligence-v2/utils/canal-do-anexo'

// [Lote de front A, T004a] chave do canal (`canalDoArquivo`, ASCII, sem
// acento) → rótulo humano do destino do upload. Mostrado ao lado do nome do
// anexo (owner: "só aparece no chat os arquivos de imagens" — o nome sozinho
// não dizia PARA ONDE o anexo ia). Extensão sem canal reconhecido não entra
// aqui — `composer-attachments.vue` cai no nome cru quando a chave falta.
export const DESTINO_ANEXO_LABEL = {
  [CANAL_CARTEIRA]: 'carteira',
  [CANAL_TRANSCRICAO]: 'transcrição',
}

/**
 * Rótulos do chat IA redesenhado — blocos do assistente, composer e sidebar.
 *
 * Arquivo separado de `constants/Text.js` porque aquele já está em 478 linhas e
 * o limite do repo é 500; nada novo do chat entra lá. Aqui fica só copy: nenhum
 * literal de UI pode nascer dentro de componente.
 */

// Último degrau do rótulo de agente da bolha de ERRO do turno (`agent-badge`,
// estado `erro`): o turno morreu antes do `agent-selected`, então não há agente
// resolvido — a cascata em `chat-message.vue::rotuloDoAgenteSemResolucao` tenta
// o agente ativo da conversa, depois o principal da persona, e só então cai
// aqui. Nunca um traço: "—" ao lado da marca lia como campo quebrado.
export const ROTULO_AGENTE_GENERICO = 'Assistente AAWZ'

// O turno recusado por saldo esgotado (`status: 402` no envelope,
// `gate_creditos.py`). A `message` do servidor fica na bolha; o que é do FRONT
// é dizer ONDE está a saída — o botão "Meus créditos" do rodapé da lateral e o
// mesmo modal, aberto daqui — e a faixa/placeholder do composer bloqueado.
export const SALDO_ESGOTADO = Object.freeze({
  ORIENTACAO:
    'Para recarregar, clique em "Meus créditos" no rodapé da lateral, ou abra por aqui.',
  ABRIR_CREDITOS: 'Abrir Meus créditos',
  AVISO: 'Seus créditos acabaram. Não é possível enviar novas perguntas.',
  ACAO: 'Meus créditos',
  PLACEHOLDER: 'Recarregue seus créditos para enviar novas perguntas',
})

// A queda do Modo Agente para o Modo Chat quando o backend recusa o
// `POST /runs` por FUNCIONALIDADE não contratada (`[SEM_FUNCIONALIDADE]`,
// `utils/recusa-de-funcionalidade.js`).
//
// ★ A régua da copy é a do defeito que ela fecha (medido em 17/09/2026): o
// consultor via `"[SEM_FUNCIONALIDADE] IA - Agente nao habilitada para este
// escritorio. {'id_escritorio': 83} Contate o suporte."` — código de exceção,
// dicionário Python e um pedido de suporte para algo que NÃO quebrou. Aqui a
// mensagem foi despachada; o que mudou é só o modo. Por isso a frase diz o
// efeito (seguiu pelo chat comum) e não pede ação nenhuma: não há nada que o
// consultor possa fazer, e mandá-lo ao suporte por uma degradação que
// funcionou é ruído.
export const DEGRADACAO_PARA_MODO_CHAT = Object.freeze({
  AVISO:
    'Os recursos de agente não estão liberados para o seu escritório. Sua mensagem seguiu pelo chat comum.',
})

// O cartão que propõe gravar quem é a pessoa (spec-009, US-3/AC-3.2).
//
// ★ A copy diz o que ACONTECE no clique, nunca "confirma?" — é o clique que
// autoriza a gravação (AC-3.3: a palavra falada nunca confirma), e um rótulo
// vago transforma consentimento em reflexo. "Agora não" em vez de "Cancelar":
// não há nada a cancelar, a proposta simplesmente não é gravada.
export const CONFIRMAR_PERFIL = Object.freeze({
  TITULO: 'Guardar quem é você',
  DESCRICAO:
    'Confira o que eu entendi. O que você salvar fica na Memória da IA e vale nas próximas conversas. Você pode editar ou apagar quando quiser.',
  CAMPO_NOME: 'Como te chamar',
  CAMPO_PAPEL: 'O que você faz',
  CAMPO_OBJETIVO: 'O que você busca aqui',
  SALVAR: 'Salvar na memória',
  DISPENSAR: 'Agora não',
  SALVO: 'Guardado na Memória da IA.',
  // Toda mensagem de erro carrega a próxima ação (regra do repo).
  ERRO_SALVAR: 'Não foi possível guardar agora. Tente de novo em instantes.',
  // A leitura do perfil ANTES de gravar não é detalhe: o `PUT /memoria/perfil`
  // SUBSTITUI `extras` inteiro, então gravar sem saber o que já existe apagaria
  // o resto. Sem essa leitura, não se grava — ver o mixin.
  ERRO_LER_PERFIL:
    'Não foi possível ler sua memória agora, e por isso não gravei nada. Tente de novo em instantes.',
  VAZIO: 'Preencha ao menos um campo para guardar.',
})

// A linha ÚNICA do raciocínio (`raciocinio-linha.vue`, 11/09/2026) — o passo
// atual com shimmer enquanto o turno roda, o resumo quando termina, a lista
// dos passos ao expandir. Substituiu o card "Raciocínio da IA" (que tinha o
// rótulo fixo e a lista sempre à vista) e o rótulo de fase do rodapé.
export const RACIOCINIO_LINHA = {
  // O título FIXO da linha concluída (`Raciocínio da IA ⌄  7 etapas · 4,2s`,
  // formato da referência) — enquanto roda, o texto é o passo atual.
  titulo: 'Raciocínio da IA',
  // O auxiliar do resumo: N etapas (após o agrupamento de repetidos) e a
  // duração com UMA casa (`duracao_s` da mensagem, ou o cronômetro); sem
  // duração, só as etapas.
  resumo: (etapas, segundosFormatados) =>
    `${etapas} ${etapas === 1 ? 'etapa' : 'etapas'}` +
    (segundosFormatados ? ` · ${segundosFormatados}s` : ''),
  // Nome acessível do botão que abre/fecha a lista dos passos.
  ariaLista: 'Passos do raciocínio da IA',
  // Contador ao lado do passo, em segundos decorridos (inteiros, ao vivo).
  segundos: (segundos) => `${segundos}s`,
  // Repetições consecutivas do mesmo passo agrupadas numa linha só.
  vezes: (n) => `${n}×`,
}

// ★ [spec-008 T064] "Pensando por X" — o tempo que o turno levou, REGISTRADO
// na mensagem depois que ele termina (é o que o ChatGPT e o Claude fazem: o
// cabeçalho colapsado do raciocínio vira "Thought for 8 seconds"). Enquanto o
// turno roda quem conta é o cronômetro ao vivo (`segundos`, acima) — este é o
// fecho, e só existe depois do `end`.
//
// A montagem mora em `components/chat-message/chat-message.passo.js`
// (`rotuloDeTempoDoTurno`), junto do limiar; aqui ficam só as PALAVRAS.
export const TEMPO_DO_TURNO = {
  prefixo: 'Pensando por',
  // Abaixo de um minuto, segundos inteiros: "42 s".
  segundos: (s) => `${s} s`,
  // A partir de um minuto, "1 min 12 s" — e "2 min" quando não sobra segundo,
  // porque "2 min 0 s" é ruído.
  minutos: (m, s) => (s > 0 ? `${m} min ${s} s` : `${m} min`),
}

// Os rótulos das tools em PASSADO, para a lista da linha de raciocínio depois
// do `end` ("Consultei o cliente" em vez de "Consultando cliente..."). Chave =
// nome da tool (o mesmo de `TOOL_LABELS`); tool sem entrada cai no rótulo
// presente sem as reticências (`utils/raciocinio-passos.js`).
export const TOOL_LABELS_PASSADO = {
  get_cliente_resumo: 'Consultei o cliente',
  get_cliente_dados: 'Consultei os dados do cliente',
  get_cliente_360: 'Consultei a visão 360 do cliente',
  search_cliente_by_nome: 'Busquei o cliente',
  list_clients: 'Consultei os clientes',
  list_clientes_carteira: 'Consultei os clientes da carteira',
  get_ativos_consolidados: 'Consultei os ativos',
  get_rentabilidade_carteira: 'Consultei a rentabilidade',
  list_tarefas: 'Consultei as tarefas',
  list_oportunidades: 'Consultei as oportunidades',
  list_atendimentos_recentes: 'Consultei os atendimentos recentes',
  list_alertas_atrasados: 'Consultei os alertas atrasados',
  list_crm_atividades_sugeridas: 'Consultei as atividades sugeridas',
  get_funil_vendas: 'Consultei o funil de oportunidades',
  get_funil_leads: 'Consultei o funil de leads',
  list_crm_clientes_prioridade_relacionamento:
    'Consultei os clientes prioritários',
  get_situacao_escritorio: 'Consultei a situação do escritório',
  list_catalogo_servicos: 'Consultei o catálogo de serviços',
}

// ★ [review #2364, item D — owner: "a maioria do tempo fica com o
// Pensando... ativado, não tem detalhamento"] O rótulo humano de cada nó do
// Supervisor (`node-status`, `constants/NosDoSupervisor.js`) — o degrau da
// linha de raciocínio entre a tool em voo e a fase
// (`utils/rotulo-do-turno.js::rotuloDoNoDoTurno`). Mesma forma dos
// `TOOL_LABELS` (gerúndio + reticências), porque é a mesma linha.
//
// ⚠️ De propósito SEM entrada: os nós de infra (`initialize_run`,
// `authenticate_and_authorize`, `complete_run`, `emit_learning_event` —
// "autenticando" ao consultor é ruído) e os condicionais que são no-op na
// maioria dos turnos (`enter_advisory_hub`, `fallback_apos_tentativa`,
// `approval_gate`, `execute_approved_actions` — "gravando o que você
// aprovou" num turno de leitura seria mentira). Nó sem entrada é PULADO pela
// cascata, nunca vira o slug cru.
export const ROTULO_DO_NO_DO_TURNO = Object.freeze({
  understand_request: 'Entendendo o pedido...',
  load_global_context: 'Reunindo o contexto...',
  route_domains: 'Escolhendo o especialista...',
  build_delegation_plan: 'Montando o plano...',
  execute_domain_agents: 'Consultando o especialista...',
  aggregate_results: 'Reunindo as respostas...',
  cross_domain_reasoning: 'Cruzando as respostas...',
  validate_answer: 'Conferindo a resposta...',
  determine_actions: 'Definindo os próximos passos...',
  compose_response: 'Escrevendo a resposta...',
})

// Classe do shimmer — contrato cross-arquivo com `design/_ia-shimmer.sass`.
// Uma const só: a string estava repetida em 4 componentes (AC-9.5).
export const CLASSE_SHIMMER_TEXTO = 'ia-shimmer-text'

// Bloco "Deliberação do painel" — uma linha por área da `mesa`.
export const BLOCO_PAINEL = {
  titulo: 'Deliberação do painel',
  emAndamento: (concluidas, total) => `${concluidas}/${total} áreas`,
  concluido: (total) =>
    `${total} ${total === 1 ? 'área' : 'áreas'} · concluída`,
}

// Estado de cada área. As chaves são o enum `ESTADO_*` de `core/sse.py`
// (backend) — trocar uma faz a linha renderizar rótulo vazio sem que lint ou
// exceção peguem. `aguardando` é estado LOCAL, anterior ao evento `abertura`:
// o backend nunca o emite, porque `abertura` já põe todas em `analisando`.
export const ESTADO_AREA_LABEL = {
  aguardando: 'Aguardando na fila',
  analisando: 'Analisando o caso...',
  concluido: 'Análise concluída',
  // Fallback: a linha mostra o `motivo` que veio no evento; este rótulo só
  // aparece quando o backend manda `indisponivel` sem motivo.
  indisponivel: 'Área indisponível',
}

// Papel da área na mesa: `lead: true` conduz, as demais apoiam.
export const BADGE_MESA = {
  conduz: 'CONDUZ',
  apoia: 'APOIA',
}

// Bloco "Próximos passos" — `parecer.proximos_passos`, lista numerada e SEM
// badge (o backend devolve `list[str]`, gap G4). O "Estruturando próximos
// passos..." que o bloco mostrava enquanto o parecer não chegava SAIU em
// 11/09/2026: era um segundo indicador no meio da resposta; quem diz
// "Consolidando parecer" é a linha única do raciocínio.
export const BLOCO_PROXIMOS_PASSOS = {
  titulo: 'Próximos passos',
}

// Progress do `plano` (spec-001 `preparacao-reuniao`, D8.2) — os passos de
// negócio de um atalho em execução ("Reunindo o histórico", nunca "executando
// tool 3"). Título fixo do bloco; a lista de passos vem do evento SSE.
export const BLOCO_PLANO_DA_PREPARACAO = {
  titulo: 'Preparando',
}

// `estado` de cada item de `plano.itens` — espelho de
// `core/plano_vivo.py`/`contracts/api.md` §3. Congelado: usado como CHAVE em
// mapas de ícone/estilo do `plano-progress.vue`, não como texto livre.
export const ESTADO_DO_PASSO_DO_PLANO = Object.freeze({
  PENDENTE: 'pendente',
  EM_ANDAMENTO: 'em_andamento',
  CONCLUIDO: 'concluido',
  FALHOU: 'falhou',
})

// Bloco "Perguntas em aberto" — `parecer.perguntas_em_aberto`.
export const BLOCO_PERGUNTAS_ABERTAS = {
  titulo: 'Perguntas em aberto',
  legenda: 'Responder destrava as próximas etapas',
}

// Formulario da tool `perguntar_ao_gestor`. NAO confundir com o bloco acima:
// aquele e a lista read-only do parecer do painel; este e o canal onde o gestor
// de fato escreve, e as respostas voltam ao modelo emparelhadas com a pergunta.
export const BLOCO_PERGUNTAS_AO_GESTOR = {
  titulo: 'Preciso que você defina',
  legenda:
    'Responda o que souber agora. O que ficar em branco continua em aberto.',
  enviar: 'Enviar respostas',
  placeholder: 'Sua resposta',
  semResposta: 'Em aberto',
}

// Bloco dos servicos do catalogo que a IA citou (tool `citar_servicos`).
// O titulo NAO e "Proximos passos": esse nome ja e do bloco do painel, e os
// dois aparecem na MESMA resposta — dois cabecalhos identicos com conteudo
// diferente eram lidos como duplicata. Aqui sao servicos do catalogo AAWZ,
// cada um com seu estado no ciclo; la sao as acoes que o painel recomendou.
export const BLOCO_SERVICOS_CITADOS = {
  titulo: 'Serviços citados',
}

/**
 * ★ [19/09/2026] A conversa que NÃO pôde ser exibida — o bloco que ocupa o
 * lugar do trilho quando uma bolha estoura no render (`messages.vue`,
 * `errorCaptured`). Em branco, o consultor lê "conversa vazia" e continua
 * perguntando: no QA da spec-006 foram 13 turnos cobrados sem renderizar.
 * O corpo diz o que fazer, nunca só o que houve.
 */
export const TRILHO_QUEBRADO = {
  titulo: 'Não foi possível exibir esta conversa',
  corpo:
    'Algo quebrou ao montar as mensagens. Recarregue a página para continuar. Até lá, o envio está bloqueado para você não pagar por uma pergunta que não aparece.',
}

/**
 * ★ [owner, 21/09/2026] A pílula "↓ Novas linhas" no rodapé do trilho: durante
 * a resposta o trilho SEGUE o fim (como o Claude.ai); quem sobe para reler
 * desgruda, e a pílula é o caminho de volta para o que continua chegando.
 */
export const NOVAS_LINHAS = {
  rotulo: 'Novas linhas',
  aria: 'Ir para o fim da resposta, que continua chegando',
}

export const COMPOSER_CHAT = {
  // O "+" fica desabilitado até o gap G3: envio não tem canal para arquivo, e
  // anexo que some depois de adicionado é dado falso pela via contrária.
  anexosEmBreve: 'Anexos em breve',
  // ★ [spec-016 T023] O upload REAL existe agora (endpoint de carteira do
  // Fee Fixo) — mas só quando há cliente em foco. Este aviso não é mais
  // incondicional: só aparece quando SOBRA algum anexo sem `upload_id` (sem
  // cliente, ou o upload falhou). Ver `avisoDeAnexo` em `send-message.vue`.
  // ★ [17/09/2026 → revertido em spec-009-anexo-sem-trava, T009] O aviso
  // chegou a ser também o MOTIVO do botão travado — o envio NUNCA mais
  // trava por causa de anexo (US-2). A frase deixou de prometer "para
  // enviar a mensagem": o anexo que não subiu simplesmente NÃO vai junto
  // (o turno parte sem ele, e o texto do turno diz isso — ver
  // `COMPOSER_CHAT.anexosNaoIncluidos`); remover no "×" é só o jeito de
  // desistir dele de vez, não de "destravar" nada.
  anexosNaoEnviados:
    'Algum anexo não subiu e não vai nesta mensagem. Remova-o no "×" ou tente de novo.',
  // O motivo mais comum de "não enviado": nenhum cliente em foco no momento
  // do anexo. A rota de upload (`carteira-chat/upload`) exige o cliente
  // ANTES de aceitar o arquivo — sem ele, não há para onde subir.
  //
  // ★ [Decisão G-4 do team-lead] Redação pedida explicitamente, e reforçada
  // pelo spec-016 T035: além do seletor de escopo, agora existe um segundo
  // caminho de verdade para o composer aprender o cliente — o `abrir_cliente`
  // via chat (`clienteDaConversaAtual`, `view-chat.vue:idClienteEmFoco`).
  //
  // ★ [STRESS r1, G-9] "ou selecione-o" (redação original) não dizia ONDE —
  // o gesto real é o ícone `user-2` do `EscopoSelect` (`escopo-select.vue`,
  // tooltip "Cliente em foco: ..."), que fica ao lado dos outros chips do
  // composer (agente/modelo). Sem nomear o ícone, o gesto não era achado na
  // tela — a medição também mostrou que entrar na Central de IA a partir do
  // perfil de um cliente não herdava o cliente sozinho (corrigido à parte em
  // `view-chat-imersivo.vue::sairDoContextoDeCliente` — CLAUDE.md do módulo);
  // esta frase continua valendo para quem chega sem vir de um perfil.
  // ★ [17/09/2026] A frase não cita mais "peça 'abre o cliente X' no chat":
  // com um anexo pendente o envio fica TRAVADO (`send-message.vue::canSend`),
  // então esse caminho — que dependia de mandar uma mensagem — não existe
  // mais enquanto o anexo estiver na fila. Sobram os dois gestos que de fato
  // funcionam agora: escolher o cliente no chip, ou tirar o anexo da fila.
  // ★ [MUST-1 do review do PR #2319 → REESCRITO em T014/T015, spec-009 W6]
  // O turno de ATALHO (`/pré-reunião`, `/panorama`) não tem campo de anexo no
  // transporte — e, mais fundo, não tem QUEM leia o anexo: ele fecha SEM
  // MODELO (`usecase_ia_crm_chat.py:2416-2425`, medido em 20/09/2026).
  //
  // Entre 17 e 20/09 a copy anterior (`atalhoComAnexo`) era motivo de
  // BLOQUEIO: "remova o anexo no × ... para enviar só a mensagem", com o
  // botão desabilitado. Era a mesma trava que o CTO mandou tirar (print:
  // `/fee-fixo` + PDF de prospect, envio preso). O desenho agora é avisar e
  // PRESERVAR: o atalho parte, o anexo continua na fila para o turno
  // seguinte, e a frase diz as duas coisas — o que acontece com o arquivo, e
  // o gesto de quem quer mandá-lo AGORA (Backspace, porque o chip não tem
  // "×"). ⚠️ Nunca voltar a redigi-la como motivo de trava: quem não pode ir
  // junto é DITO, não bloqueia (`avisoDeAnexo`, `send-message.vue`).
  atalhoNaoLevaAnexo:
    'O comando escolhido não leva anexo. Ele fica na fila para a próxima mensagem. Para mandá-lo agora, apague o comando com Backspace na caixa vazia.',
  // ★ [spec-009-anexo-sem-trava, T008] Desde que o canal de CARTEIRA passou
  // a subir sem cliente pelo canal de análise, o ÚNICO caso que ainda cai em
  // `sem_cliente` é o de TRANSCRIÇÃO (`.txt`/`.md`/`.vtt`/`.docx`) — daí a
  // frase não falar mais em "carteira". E o envio NÃO trava mais por isso
  // (US-2): o texto acompanha o aviso na tela, mas o turno parte sem aquele
  // anexo enquanto o cliente não é resolvido.
  anexoSemCliente:
    'Para anexar esta transcrição, é preciso um cliente em foco: clique no ícone de cliente ao lado dos demais chips do composer, ou remova o anexo no "×" para não incluí-lo.',
  // ★ [T009, spec-009] Upload ainda em voo no instante do clique de enviar —
  // como o envio nunca espera mais (US-2), este é o motivo de "não incluído"
  // quando o item está `enviando`.
  anexoAindaSubindo: 'O upload ainda estava em andamento quando a mensagem foi enviada',
  // ★ [T009, spec-009] O turno DIZ, no próprio texto, o que ficou de fora e
  // por quê — nunca em silêncio. `itens` é `[{nome, motivo}]`
  // (`send-message-anexos-upload-mixin.js::itensNaoIncluidosNoEnvio`).
  anexosNaoIncluidos: (itens) =>
    (itens || [])
      .map((item) => `"${item.nome}" não foi incluído (${item.motivo}).`)
      .join(' '),
  // ★ teste-11 (feedback do owner, 09/09/2026): o anexo GRAVA carteira no
  // cadastro de um cliente, e até aqui subia sem dizer de quem. Medido na
  // conversa `51e550c2` (escritório 90083): o consultor criou um cliente por
  // engano, criou o certo em seguida e anexou o extrato — os 4 arquivos foram
  // para o PRIMEIRO, e ninguém viu. O destino agora é dito ANTES do envio,
  // ao lado das miniaturas. `{cliente}` vem da mesma fonte que decide o id do
  // upload (`view-chat.vue::nomeClienteAnexo`) — nomear por uma fonte e
  // enviar por outra seria o mesmo defeito com uma frase por cima.
  destinoDoAnexo: 'A carteira anexada vai para {cliente}',
  // Tooltip do selo de erro sobre a miniatura (composer-attachments.vue).
  erroUploadAnexo: 'Falha ao enviar este anexo. Ele não vai para a IA',
  // [spec-008 T028] Nome acessível da miniatura quando a fila está em modo
  // ENTREGA (`com-download`): ali o clique baixa, e o `title` do anexo do
  // composer ("nome — destino") descreveria a viagem contrária.
  baixarArquivo: (nome) => `Baixar ${nome}`,
  // ★ [STRESS r1, G-8] Antes destas duas, um arquivo vazio (0 B) ou acima do
  // teto (`MAX_BYTES_POR_ANEXO`) era descartado por `arquivoAceito` em
  // SILÊNCIO — sem miniatura, sem aviso nenhum. `nome`/`tamanho`/`limite` vêm
  // do arquivo real (nunca hardcode) — ver `avisarArquivosRejeitadosPorTamanho`
  // em `send-message.vue`.
  anexoVazio: (nome) =>
    `O arquivo "${nome}" está vazio (0 B) e não foi anexado.`,
  anexoExcedeLimite: (nome, tamanho, limite) =>
    `O arquivo "${nome}" (${tamanho}) excede o limite de ${limite} por anexo e não foi anexado.`,
  // Descrição do "+" e do botão QUANDO o anexo está liberado. Diz o que a
  // pessoa pode escolher, que é o que ela precisa saber na hora de clicar —
  // "Anexos em breve" ali seria mentira com a trava aberta.
  anexosAceitos: 'Imagem, PDF ou planilha',
  // Reusa o aviso que já existe no módulo — não duplicar a copy regulatória.
  disclaimer: AVISO_NAO_ACONSELHAMENTO,
  // Raciocínio estendido do modelo. Em PT-BR como o resto da UI — estava
  // 'Thinking ON'/'Thinking OFF' inline no template.
  raciocinioLigado: 'Raciocínio ativo',
  raciocinioDesligado: 'Raciocínio desativado',
  adicionarAnexo: 'Adicionar anexo',
  removerAnexo: 'Remover anexo',
  enviar: 'Enviar mensagem',
  cancelar: 'Cancelar envio',
  // ★ [STRESS r2, N-1] Backstop de `enviarMensagem` (`view-chat.vue`) — o
  // botão já vira "parar" com `turnoEmVoo`; este texto só aparece se um
  // clique escapar dessa janela (Enter antes do próximo render).
  turnoEmVooAviso:
    'A resposta anterior ainda está em andamento. Aguarde ou clique em Parar para cancelar.',
  // ★ [19/09/2026] A thread MORREU no render e o composer continuava aceitando
  // turno: no QA da spec-006 foram 13 envios que chegaram ao backend, gastaram
  // modelo (4.986,80 → 4.974,44 em créditos) e nunca apareceram na tela. Tela
  // vazia que aceita input é a pior combinação — parece funcionando e cobra.
  // A faixa é BLOQUEANTE (sem "×"): o único caminho de volta é recarregar.
  trilhoQuebradoFaixa:
    'Esta conversa não pôde ser exibida. Recarregue para continuar. Enquanto isso, o envio está bloqueado.',
  trilhoQuebradoAcao: 'Recarregar',
  // O backstop do envio, para o Enter que escapa antes do próximo render.
  trilhoQuebradoAviso:
    'A conversa não está sendo exibida. Recarregue a página antes de enviar, ou a pergunta é cobrada sem aparecer.',
  // ⚠️ As três frases abaixo encurtaram (26/08): a combinada quebrava em duas
  // linhas na toolbar e engordava a régua do composer. "nesta conversa" ficou
  // só na combinada, que é a que aparece na prática — os dois locks caem
  // juntos assim que a conversa tem histórico.
  // O backend fixa o modelo da conversa; o hint só torna a regra visível.
  modeloFixo: 'Modelo fixo',
  // Rótulo do seletor na janela entre montar o composer e `/supported-models`
  // responder. Substituiu o id CRU, que virou id ERRADO quando
  // `claude-sonnet-4-6` saiu da lista (Q7-A08) — um substantivo neutro nunca
  // vence a validade, e o id certo aparece assim que a lista chega.
  modeloIndefinido: 'Modelo',
  // Espelho do lock de PERSONA de `usecase_ia_crm_chat.py`: ao retomar uma
  // conversa o backend sobrescreve a persona enviada, então trocar de agente no
  // meio dela seria ignorado em silêncio — o chip trava junto com o modelo.
  agenteFixo: 'Agente fixo',
  // Os locks nascem da mesma conversa e caem juntos: frases lado a lado
  // repetindo "nesta conversa" eram lidas como aviso duplicado, e por isso o
  // composer troca todas por esta quando mais de um vale.
  // ⚠️ Nome sem enumerar quem trava (era `agenteEModeloFixos`): o CLIENTE
  // entrou como terceiro em 26/08, e um nome que lista os locks fica errado a
  // cada um que entra.
  fixosNaConversa: 'Fixos nesta conversa',
  // Espelho do lock de CLIENTE: `usecase_ia_crm_chat` vincula o cliente na
  // CRIAÇÃO da conversa e não re-vincula quem retoma, então trocar de escopo no
  // meio dela seria ignorado em silêncio — o chip trava junto com os outros.
  escopoFixo: 'Cliente fixo',
  // Texto do envio SÓ COM ANEXO (`send-message.vue::textoDoEnvio`): ninguém
  // digitou nada, então a fala tem que nomear o que foi junto — para o modelo
  // e para quem relê a conversa depois. ★ [17/09/2026] Era também o texto do
  // turno de continuação AUTOMÁTICO (T004b), removido: envio é sempre gesto
  // de quem escreve.
  continuacaoDeAnexos: (nomes) =>
    'Anexos enviados: ' + (nomes || []).join(', ') + '.',

  // Seletor de voz do Modo de Voz (spec-016, [US-1]/[US-3]). O rótulo do
  // catálogo em si (nome de cada voz) vem SEMPRE do servidor — estas são só
  // as strings de CHROME do componente, nunca substituto da lista curada.
  vozRotulo: 'Voz',
  vozTooltip: 'Voz da IA nesta conversa',
  vozOuvirAmostra: (rotulo) => 'Ouvir amostra de ' + rotulo,
  vozAmostraFalha: 'Não foi possível tocar a amostra desta voz agora.',
}

// O contador "N de M" no rótulo dos blocos do painel do cliente em foco
// (`utils/cliente-em-foco/contador-de-lista.js`): total exato ("2 de 5") ou
// ABERTO, quando só se sabe que há mais além do teto ("2 de 3+").
export const CONTADOR_DE_LISTA = {
  total: String,
  parcial: (exibidos, total) => `${exibidos} de ${total}`,
  aberto: (exibidos) => `${exibidos} de ${exibidos + 1}+`,
}

// Índice das mensagens do usuário na borda do trilho (`chat-indice-mensagens`,
// só na visão imersiva). O cabeçalho do popover segue o molde de "Fontes":
// caixa alta pequena vem do CSS, aqui é sentence case.
export const INDICE_MENSAGENS = {
  titulo: 'Mensagens',
  // Nome acessível do ativador — os traços são decorativos (`aria-hidden`).
  gatilho: (total) => `Índice da conversa: ${total} mensagens suas`,
}

// ★ [#3 do review do PR #2342] A conversa que não abriu. O `GET /messages`
// devolvia 404 (apagada noutra aba, id de outro escritório) e a tela ficava
// com o thread VAZIO e a conversa marcada como selecionada — sem uma linha
// dizendo o que houve. Fallback só para quando o servidor não manda `message`
// (queda de rede): quando manda, é o texto DELE que vai à tela.
export const ERRO_AO_ABRIR_CONVERSA =
  'Não foi possível abrir esta conversa. Ela pode ter sido apagada. Atualize a lista ou comece uma nova.'

// Abaixo disto o índice não aparece: com uma pergunta só o trilho inteiro cabe
// numa rolagem e o índice seria chrome sem função (pedido do usuário,
// 11/09/2026 — nasceu 2, subiu para 3 e voltou a 2 no mesmo dia).
export const MINIMO_DE_MENSAGENS_PARA_INDICE = 2

// ★ O NOME que o cliente final lê para o "artefato" (o cartão de dados que a
// IA produz e que abre no painel lateral). Decisão do usuário (14/09/2026):
// "Entrega" — "artefato" é jargão de engenharia. Toda copy visível deriva
// DAQUI (índice, card, painel); os identificadores do código (componentes
// `artefato-*`, store `artefato.js`, classes CSS) continuam "artefato".
// Para renomear de novo, mude só estas quatro strings.
export const TERMO_ARTEFATO = Object.freeze({
  SINGULAR: 'Entrega',
  PLURAL: 'Entregas',
  singular: 'entrega',
  plural: 'entregas',
})

// Copy do card de prévia (`artefato-preview-card`) e do painel lateral
// (`artefato-painel`), derivada do termo acima.
export const ARTEFATO_COPY = Object.freeze({
  ABRIR: `Abrir ${TERMO_ARTEFATO.singular}`,
  FECHAR: `Fechar ${TERMO_ARTEFATO.singular}`,
  CARREGANDO: `Carregando ${TERMO_ARTEFATO.singular}...`,
})

// [spec-008 T028] As entregas de arquivo de UM turno, na bolha do assistente.
// Mesma fila de miniaturas do composer (`ComposerAttachments somente-leitura`),
// com download por item — o agente produz a planilha/apresentação/PDF e é aqui
// que o consultor a baixa.
export const ENTREGAS_DA_MENSAGEM = Object.freeze({
  TITULO: TERMO_ARTEFATO.PLURAL,
  // A falha do download nunca é silenciosa, e a frase traz a próxima ação
  // (regra "Mensagens de Erro: Sempre Incluem a Solução"). Só entra quando o
  // servidor não mandou motivo próprio — 403/404 vêm com `message`.
  ERRO_SEM_MENSAGEM: `Não foi possível baixar esta ${TERMO_ARTEFATO.singular}. Tente novamente mais tarde.`,
})

// Índice "Entregas desta conversa" (`chat-indice-artefatos`, só na visão
// imersiva com o modo artefato ligado): régua de tiles no canto superior
// direito do trilho que abre o menu (condensado) no mesmo lugar.
export const INDICE_ARTEFATOS = {
  TITULO: `${TERMO_ARTEFATO.PLURAL} desta conversa`,
  // Cabeçalho do cartão aberto (print do usuário, 14/09/2026): curto, com a
  // contagem ao lado; o longo fica no `aria-label`/`title` da régua e do menu.
  TITULO_CURTO: TERMO_ARTEFATO.PLURAL,
  // Nome acessível da RÉGUA (visão compacta, sem texto): título + contagem.
  ROTULO_ACESSIVEL: (total) =>
    total === 1
      ? `${TERMO_ARTEFATO.PLURAL} desta conversa: 1 ${TERMO_ARTEFATO.singular}`
      : `${TERMO_ARTEFATO.PLURAL} desta conversa: ${total} ${TERMO_ARTEFATO.plural}`,
  // Cabeçalho do menu: minimizar leva à RÉGUA (o estado mínimo desde
  // 14/09/2026) e grava a preferência. `aria-label` + `title`.
  MINIMIZAR: 'Minimizar',
  // Régua: o chevron do topo abre o menu (na faixa, ou POR CIMA do chat).
  // No menu sobreposto o mesmo lugar do cabeçalho volta à régua.
  EXPANDIR: 'Expandir',
  RECOLHER: 'Recolher',
}

// Painel da conversa (atrás de `PAINEL_PROGRESSO_CONTEXTO_ATIVO`): o cartão
// flutuante no canto do trilho (Progresso / Entregas / Contexto); a entrega
// abre no aside. §5.1 do CLAUDE.md.
// Cabeçalho do plano na bolha: "Usando skill /x" quando o turno veio de um comando.
export const PLANO_DO_TURNO = Object.freeze({
  titulo: 'Plano',
  usandoSkill: (comando) => 'Usando skill ' + comando,
  etapas: (feitas, total) => feitas + ' de ' + total + ' etapas',
})

export const PAINEL_DO_CHAT = {
  ARIA: 'Painel da conversa',
  PROGRESSO: 'Progresso',
  ENTREGAS: TERMO_ARTEFATO.PLURAL,
  CONTEXTO: 'Contexto',
  CLIENTE: 'Cliente',
  ESCRITORIO_INTEIRO: 'Escritório inteiro',
  AGENTE: 'Agente',
  FONTES: 'Fontes',
  EM_ANDAMENTO: 'Em andamento',
  CONCLUIDO: 'Concluído',
  EXPANDIR: 'Abrir o painel da conversa',
  RECOLHER: 'Recolher o painel da conversa',
  MOSTRAR_ETAPAS: 'Mostrar as etapas',
  OCULTAR_ETAPAS: 'Ocultar as etapas',
  MAIS_FONTES: (quantidade) => `+${quantidade}`,
  CONTAGEM_DE_ENTREGAS: (total) =>
    total === 1
      ? `1 ${TERMO_ARTEFATO.singular}`
      : `${total} ${TERMO_ARTEFATO.plural}`,
  FRACAO: (concluidas, total) => `${concluidas} de ${total} etapas`,
  CONCLUIDAS_OCULTAS: (quantidade) =>
    quantidade === 1 ? '1 concluída' : `${quantidade} concluídas`,
  STATUS_DO_PASSO: {
    pendente: 'Pendente',
    em_andamento: 'Em andamento',
    concluida: 'Concluída',
    falhou: 'Falhou',
  },
}

// Estado vazio: as duas linhas do herói serif. A segunda vem do agente
// selecionado (`Agentes.js` → `headline`), esta é só o default do componente.
export const HERO_CHAT = {
  saudacao: 'Olá',
  headline: 'Como posso te ajudar hoje?',
}

// Unidades de tamanho de arquivo. `KB` inclusive: o `size` do File vem em
// bytes e ninguém lê "1048576 B".
export const SIDEBAR_CHAT = {
  // Filtro client-side sobre as conversas já carregadas: o GET /conversations
  // não tem parâmetro de busca (gap G8). O placeholder diz isso na cara.
  buscaPlaceholder: 'Buscar nas conversas',
  // Na visão imersiva a busca de conversa mudou de casa: vive no Ctrl/Cmd+K,
  // junto com agentes e módulos. O rótulo não promete "conversas" porque o
  // palette acha mais que isso.
  buscaGlobal: 'Buscar',
  // N é o total CARREGADO (limit 30), não o total do banco — a API não devolve
  // contagem (gap G8). O rótulo não afirma ser o total.
  verTodas: (total) => `Ver todas (${total})`,
  verMenos: 'Ver menos',
  novaConversa: 'Nova conversa',
  // Teclas dos atalhos do topo — o rótulo é montado por plataforma
  // (`utils/atalho-do-teclado.js`: ⌘ no Mac, Ctrl no resto).
  teclasNovaConversa: ['Shift', 'O'],
  teclasBuscaGlobal: ['K'],
  secaoAgentes: 'Agentes',
  secaoRecentes: 'Recentes',
  // Tooltip do relógio na régua recolhida. "Ver histórico" e não "Recentes":
  // ali o botão ABRE a lateral para chegar na seção, então o rótulo descreve o
  // destino, não o nome da caixa.
  verHistorico: 'Ver histórico',
  secaoAgrupar: 'Agrupar por',
  vazio: 'Nenhuma conversa ainda',
  // Busca sem resultado é diferente de lista vazia: a primeira tem saída
  // (limpar o termo), a segunda não.
  buscaSemResultado: 'Nenhuma conversa com esse termo entre as carregadas',
  erro: 'Não foi possível carregar suas conversas.',
  tentarDeNovo: 'Tentar de novo',
  // Os dois modos com dado não têm nada a dizer quando o modo está ativo e
  // não há conversa: o grupo simplesmente não existe.
  semGrupos: 'Nada para agrupar ainda',
  // Pasta aberta e vazia: sem esta linha ela parece quebrada em vez de vazia.
  pastaVazia: 'Nenhuma conversa nesta pasta',
  // ★ Painel de situação do escritório (pendência #266). "Indisponíveis" e não
  // "sem dados": o endpoint FALHOU, e as duas coisas pedem reações diferentes —
  // uma tem saída (tentar de novo), a outra não. Antes desta linha a coluna
  // caía nos números do catálogo e mentia com cara de dado.
  insightsIndisponiveis: 'Indicadores indisponíveis',
  insightsIndisponiveisAjuda:
    'Não foi possível carregar o painel do escritório. Tente novamente em instantes.',
  // Endpoint respondeu, e não há indicador para este escritório. Sem saída — o
  // botão de tentar de novo aqui seria um convite a repetir o mesmo nada.
  insightsVazio: 'Nenhum indicador para este escritório ainda',
  // Confirmações destrutivas. São CÓPIA VERBATIM da copy de `conversations.vue`
  // (o histórico do popup, congelado por D-8): a mesma frase em duas superfícies
  // diverge na primeira vez que alguém ajusta um dos lados, então a sidebar lê
  // daqui e a migração do popup é trocar dois literais por estas chaves.
  cancelar: 'Cancelar',
  removerConversa: 'Remover',
  removerConversaTitulo: 'Remover conversa',
  removerConversaTexto:
    'Deseja remover esta conversa? Esta ação não pode ser desfeita.',
  // Mesmo par acima, pluralizado pra seleção em lote (ctrl+click nas
  // "Recentes" da imersiva) — `removerConversa` (o rótulo "Remover") é
  // reaproveitado como texto do botão de confirmar nos dois casos.
  removerConversasLoteTitulo: 'Remover conversas',
  removerConversasLoteTexto: (n) =>
    `Deseja remover ${n} ${
      n === 1 ? 'conversa' : 'conversas'
    }? Esta ação não pode ser desfeita.`,
  excluirPasta: 'Excluir',
  excluirPastaTitulo: 'Excluir pasta',
  // As conversas não são apagadas junto — o texto diz isso para o usuário não
  // desistir de excluir a pasta achando que perde o histórico.
  excluirPastaTexto: (nome) =>
    `Deseja excluir a pasta "${nome}"? As conversas serão mantidas e movidas para "sem pasta".`,
  // Botão do painel personalizável — mesmo texto exato do editSummaryTooltip
  // (scope profissional) de `lead-summary-card.vue`, mesmo ícone (`tune`).
  personalizarPainel: 'Personalizar meu painel',
  personalizacaoPaletaTitulo:
    'Adicionar módulo: clique ou arraste para o painel',
  personalizacaoPaletaVazia:
    'Todos os módulos disponíveis já estão no seu painel.',
  personalizacaoCanvasVazio:
    'Nenhum card no seu painel. Adicione módulos acima.',
  personalizacaoOcultar: 'Ocultar',
  personalizacaoArrastar: 'Arrastar para reordenar',
}

/**
 * Os três desfechos do painel de situação do escritório na lateral imersiva.
 *
 * ⚠️ `OK` cobre também o CARREGANDO: nesse caso os cards existem (o rótulo vem
 * do catálogo) e o esqueleto mora dentro de cada um. Os outros dois não
 * desenham card nenhum — desenham AVISO, porque não há número a mostrar.
 *
 * Vocabulário compartilhado entre a view imersiva (que decide) e a lateral (que
 * desenha); string solta nos dois lados divergiria na primeira mudança.
 */
export const ESTADO_INSIGHTS = {
  OK: 'ok',
  INDISPONIVEL: 'indisponivel',
  VAZIO: 'vazio',
}

// Título dos grupos que não nascem de um registro (pasta/agente) e por isso
// não têm nome próprio vindo do backend.
export const GRUPO_SEM_PASTA = 'Sem pasta'
// Conversa cujo `context` não corresponde a nenhum agente do catálogo — de um
// agente removido, ou de uma persona que o escritório perdeu por feature flag.
// Existe para que ela apareça em algum lugar em vez de sumir da lista.
export const GRUPO_OUTROS = 'Outros'

// Ícone dos grupos sem ícone próprio. Const porque a string é lida em dois
// arquivos (o agrupador e o teste dele).
export const ICONE_GRUPO_PASTA = 'folder-outline'
export const ICONE_GRUPO_OUTROS = 'message'

// Modos de agrupamento que dependem de backend inexistente. O tooltip é o que
// separa "desabilitado porque falta dado" de "desabilitado porque quebrou".
export const MODO_AGRUPAMENTO_INDISPONIVEL = {
  // Gap G6: `id_profissional` existe na tabela e é usado no tenant-lock, mas
  // não volta no payload de GET /conversations.
  profissional:
    'Disponível quando o backend expuser o profissional de cada conversa.',
  // Gap G7: não existe vínculo conversa↔cliente no schema.
  cliente: 'Disponível quando as conversas passarem a registrar o cliente.',
}

export const MODO_AGRUPAMENTO_PASTAS = 'pastas'
export const MODO_AGRUPAMENTO_AGENTE = 'agente'
export const MODO_AGRUPAMENTO_PROFISSIONAL = 'profissional'
export const MODO_AGRUPAMENTO_CLIENTE = 'cliente'

export const MODO_AGRUPAMENTO_DEFAULT = MODO_AGRUPAMENTO_PASTAS

/**
 * Catálogo dos 4 modos do segmented "Agrupar por".
 *
 * `disabledReason` é a ÚNICA fonte de "este modo está desabilitado": o botão
 * deriva o `disabled` da presença dela. Um campo `disabled` separado abriria a
 * porta para desabilitar sem motivo — que na tela é indistinguível de bug.
 *
 * Congelado pelo mesmo motivo de `AGENTES`: as entradas são compartilhadas por
 * referência com quem renderiza, e escrever em `modo.label` corromperia o
 * módulo para o app inteiro.
 */
export const MODOS_AGRUPAMENTO = [
  {
    id: MODO_AGRUPAMENTO_PASTAS,
    label: 'Pastas',
    iconName: ICONE_GRUPO_PASTA,
    disabledReason: '',
  },
  {
    id: MODO_AGRUPAMENTO_AGENTE,
    label: 'Agente',
    iconName: 'ai-sparkle',
    disabledReason: '',
  },
  {
    id: MODO_AGRUPAMENTO_PROFISSIONAL,
    label: 'Profissional',
    iconName: 'user',
    disabledReason: MODO_AGRUPAMENTO_INDISPONIVEL.profissional,
  },
  {
    id: MODO_AGRUPAMENTO_CLIENTE,
    label: 'Cliente',
    iconName: 'people-profile',
    disabledReason: MODO_AGRUPAMENTO_INDISPONIVEL.cliente,
  },
].map(Object.freeze)

/**
 * A pergunta que o chat DIGITA pela pessoa, na primeira visita (spec-009,
 * US-2). Ela fica no campo; quem manda é a pessoa, no Enter.
 *
 * ★ Ela existe porque a boas-vindas do primeiro contato é entregue ACIMA da
 * conversa, e só renderiza depois que a pessoa manda a primeira mensagem — o
 * que na tela lia assim: ela pergunta "oq faço agora", e aparece ACIMA da
 * própria pergunta um texto que não responde nada, enquanto o modelo responde
 * embaixo. Funciona como especificado e ainda assim lê mal. Com a frase já
 * escrita no campo, a primeira mensagem é a que dá sentido à apresentação que
 * aparece acima dela.
 *
 * Trocar o texto é trocar aqui: nunca um literal dentro do `.vue`/mixin.
 */
/**
 * A faixa que explica por que o composer está travado durante a apresentação —
 * e a saída dela.
 *
 * ⚠️ **A faixa não é enfeite: é o contrato da prop `envioBloqueado`.** O
 * `send-message.vue` declara que o envio trava "e a faixa que explica o porquê
 * entra pelo slot `cabecalho`". Sem ela, a pessoa digita, aperta Enter e nada
 * acontece — sem faixa, sem snack, com o `title` do botão ainda dizendo
 * "Enviar". Ação silenciosa é o que o `UX Principle` do repo proíbe.
 *
 * ★ **E a saída não é conveniência: é o que impede um beco sem saída.** A trava
 * lê `onboardingConduzindo`, que decide pela PRESENÇA do cartão de etapa no
 * turno — independente de o cartão ter conseguido montar. Se o backend mandar
 * um campo fora do contrato, a etapa cai no `fallback-card` (sem botão de
 * concluir), `onboardingConduzindo` segue `true` e a conversa fica inutilizável
 * para sempre. Este link destrava, e vale em qualquer estado.
 */
export const APRESENTACAO_EM_CURSO = Object.freeze({
  TEXTO: 'A apresentação está conduzindo. Responda pelos botões do cartão.',
  ACAO: 'Continuar sem a apresentação',
  PLACEHOLDER: 'Responda pelo cartão acima, ou continue sem a apresentação',
})

export const PRIMEIRA_PERGUNTA_DO_PRIMEIRO_CONTATO = '/onboarding'

/**
 * ★ **Era `'Como começar?'` até 18/09/2026, e a troca é de PRODUTO.**
 *
 * A apresentação deixou de ser um texto e virou um fluxo de cinco etapas
 * (`core/onboarding/`, backend): o que o primeiro contato digita agora é o
 * COMANDO que abre esse fluxo, não uma pergunta improvisada para satisfazer
 * um gate. É o desenho da demo que o owner escolheu como referência — o
 * `/onboarding` aparece sendo digitado, e a apresentação responde a ele.
 *
 * ⚠️ **Hoje ele não vira chip no composer, e isso é sabido.** O chip é montado
 * a partir do catálogo de atalhos (`utils/comando-na-fala.js` +
 * `apiAtalhos.buscarCatalogo`), e o catálogo do backend
 * (`core/atalhos/catalogo.py`) ainda não existe nesta branch — quem reconhece
 * `/onboarding` é o servidor, pela fala (`core/onboarding/comando.py`). Com o
 * catálogo, a entrada lá faz o chip aparecer sem mexer neste arquivo.
 *
 * ⚠️ **O ritmo da digitação é medido em LETRAS**, e esta frase tem menos que a
 * anterior (11 contra 13). `MS_DA_DIGITACAO_INTEIRA_MINIMO/MAXIMO`
 * (`view-chat-primeira-pergunta.mixin.js`) travam a faixa validada ao vivo —
 * se a troca tirar a duração da faixa, o teste reprova, e o que se ajusta é
 * `MS_BASE_POR_LETRA`, nunca a faixa (ela é o que o owner aprovou na tela).
 */

// ★ [19/09/2026] O cartão `documento/panorama` — o painel lateral do atalho
// `/panorama` (spec-013 `ui-documento-panorama.schema.json`, emitido por
// `core/atalhos/documento.py::montar_documento_panorama`).
//
// ⚠️ Os quatro `resultado` do contrato NÃO são decoração: eles separam "a
// faceta foi lida e não tem o que mostrar" de "o escritório não contratou o
// módulo" e de "a leitura falhou". Colapsar os três num "sem dados" genérico
// faria o gestor ler ausência de dado onde houve ERRO — e o schema é
// explícito de que `erro_de_leitura` carrega o texto do motivo, nunca fica em
// branco (T004/A-3). A seção só cai neste rótulo quando o servidor não mandou
// `conteudo_markdown`; com texto, quem fala é o servidor.
export const PANORAMA_CARD = {
  SEM_SECOES: 'Nenhuma faceta foi lida para este panorama.',
  FONTES: 'Fontes',
  RESULTADO: {
    sem_dados: 'Sem dados no período.',
    nao_contratado: 'Módulo não contratado pelo escritório.',
    erro_de_leitura: 'Não foi possível ler esta seção agora.',
  },
}

// Os `resultado` que o cartão pinta como AVISO (âmbar/vermelho), separados do
// `disponivel`. Congelado: o enum é do contrato, não uma escolha da tela.
export const PANORAMA_RESULTADOS_DE_AVISO = Object.freeze([
  'nao_contratado',
  'erro_de_leitura',
])

/**
 * O cartão `documento/compliance-do-cliente` — o painel lateral do atalho
 * `/compliance` (spec-004-compliance-cliente, [T051], `contracts/ui-
 * components.md`). Estas SÃO decorações da tela — rótulo, descrição curta e
 * frase do vazio de cada balde — o backend só manda as CHAVES (`falha`,
 * `decisao`, `proposito`, `arquivo`) e os itens; nenhuma dessas quatro
 * chaves muda, elas são a régua de `core/compliance_cliente/regua.py`.
 *
 * ⚠️ **Balde vazio NUNCA some** (contrato §4 regra 4): a frase abaixo é o
 * que aparece no lugar da lista, com a contagem `(0)` no cabeçalho — nunca a
 * seção inteira oculta.
 */
export const COMPLIANCE_CLIENTE_CARD = Object.freeze({
  BALDES: Object.freeze({
    falha: Object.freeze({
      rotulo: 'Falha',
      descricao: 'obrigação não cumprida',
      vazio: 'Nenhuma obrigação em aberto.',
    }),
    decisao: Object.freeze({
      rotulo: 'Decisão do compliance',
      descricao: 'precisa de julgamento humano',
      vazio: 'Nenhuma decisão pendente de julgamento.',
    }),
    proposito: Object.freeze({
      rotulo: 'Certo de propósito',
      descricao: 'está assim porque foi configurado',
      vazio: 'Nada configurado nesta categoria.',
    }),
    arquivo: Object.freeze({
      rotulo: 'Para o arquivo',
      descricao: 'evidência, não ação',
      vazio: 'Nada registrado nesta categoria.',
    }),
  }),
  // A ordem de exibição dos 4 baldes — a mesma consequência da régua
  // (falha → decisão → propósito → arquivo).
  ORDEM_DOS_BALDES: Object.freeze(['falha', 'decisao', 'proposito', 'arquivo']),
  NAO_VERIFICADO: 'Não verificado',
  PROCEDENCIA: 'Montado a partir de',
  // [MUST 20/09/2026] O número que sustenta "Acima do Risco." — uma linha
  // por classe ANBIMA acima do teto do perfil. `fracao`/`maxPct` já chegam
  // formatados ("84,6%") pelo adaptador do cartão.
  CLASSE_ACIMA_DO_TETO: (classe, fracao, maxPct) =>
    `${classe}: ${fracao} (teto ${maxPct})`,
  // [R-3, review 20/09/2026] O número que sustenta "Próximo do Risco." — a
  // MESMA forma da linha acima, com o verbo que diz o fato certo: 19,5% de
  // um teto de 20% é proximidade, não estouro.
  CLASSE_PROXIMA_DO_TETO: (classe, fracao, maxPct) =>
    `${classe}: ${fracao}, perto do teto de ${maxPct}`,
  // [R-5, review 20/09/2026] A pontuação do IPS que gerou o perfil sugerido,
  // na mesma linha do achado que ela explica. Sai do item, nunca do balde
  // ARQUIVO — lá ela ficava solta, sem dizer o que sustentava.
  PONTUACAO_DO_PERFIL: (valor) => `Pontuação do IPS: ${valor}`,
  // [Q-0752, QA de tela 20/09/2026] A grandeza que faltava ao veredito "IPS
  // vencido.": o consultor não sabia se venceu ontem ou há dois anos. `dias`
  // chega POSITIVO (o componente já tirou o sinal do número do contrato, que
  // viaja negativo). Singular explícito — "1 dias" é erro de português numa
  // tela que o consultor mostra ao cliente.
  IPS_VENCIDO_HA: (dias) =>
    dias === 1 ? 'Vencido há 1 dia' : `Vencido há ${dias} dias`,
})

/**
 * O cartão `documento/revisao-de-rebalanceamento` — spec-003-rebalanceamento-
 * portfolio, [T019]/[T020], `contracts/ui-components.md` §2/§4.
 *
 * As frases de "vazio por seção" (nunca painel em branco, invariante 7 do
 * projeto) são as do wireframe (`design/wireframes.md` "Os outros 3
 * estados"), copiadas aqui uma vez — o `.vue` nunca as redigita.
 */
export const REVISAO_REBALANCEAMENTO_CARD = Object.freeze({
  TITULO: 'Revisão de rebalanceamento',
  PROCEDENCIA: 'Montado a partir de',
  // `desvio.motivo`/o `motivo` raiz — os três valores fechados do enum
  // (`api.yaml`, `core/atalhos/rebalanceamento.py`). Nunca texto livre do
  // backend: o `.vue` traduz pelo mapa, não ecoa a chave crua.
  MOTIVO_INDISPONIVEL: Object.freeze({
    cliente_sem_perfil_cadastrado: 'Sem perfil cadastrado, não há banda contra a qual comparar',
    perfil_invalido_para_o_escritorio: 'Perfil inválido para este escritório',
    cliente_sem_posicao_consolidada: 'Sem posição consolidada para comparar',
  }),
  DESVIO: Object.freeze({
    TITULO: 'Desvio contra a banda',
    VAZIO_PADRAO: 'Sem banda disponível para comparar.',
    // [MUST R-1, review 20/09/2026] `desvio.status_estrategico` chegava
    // adaptado ao front (`revisao-rebalanceamento-card-adaptado.js:55`) e
    // NUNCA era renderizado — a única frase que a tela dizia sobre a banda
    // era a da seção de opções, e ela dizia o contrário do dado.
    SITUACAO: (status) => `Situação contra a banda: ${status}`,
    // [MUST R-2] A tabela não soma 100%: classe sem alvo declarado em
    // `composicao_estrategica` não tem linha (não há alvo a exibir), mas o
    // PL dela existe — e o Edge da US-1 pede que isso seja DITO, não
    // omitido. Medido: 37,70% do PL do cliente 10817 sumia em silêncio,
    // incluindo `Fundo de Renda Fixa` (42,66%, a maior posição).
    OMITIDAS: (quantidade, pct, nomes) =>
      `${quantidade} ${quantidade === 1 ? 'classe' : 'classes'} sem alvo declarado no perfil `
      + `(${pct} do PL) fora desta tabela: ${nomes}.`,
  }),
  // [SHOULD R-6] A frase vem PRONTA do backend
  // (`documento_rebalanceamento._texto_divergencia`) e era transportada até
  // o adaptador sem nunca ser desenhada — a US-2 proíbe em letra:
  // "divergência SHALL ser exibida como aviso, não silenciada". O rótulo é
  // do front; o conteúdo, do backend.
  DIVERGENCIA_ROTULO: 'Atenção',
  CONCENTRACAO: Object.freeze({
    TITULO: 'Concentração',
    VAZIO: 'Nenhuma posição acima dos limiares.',
  }),
  CAUSA: Object.freeze({
    TITULO: 'Causa',
  }),
  OPCOES: Object.freeze({
    TITULO: 'Opções para sua revisão',
    // ⚠️ [MUST R-1] DUAS frases, e trocar uma pela outra é afirmar o
    // contrário do dado. `opcoes_de_ajuste` só produz opção quando há
    // excesso sobre o TETO ANBIMA (`opcoes_rebalanceamento.py:102`); quem
    // está fora da BANDA estratégica e dentro do teto recebe `opcoes: []`.
    // A frase única "Nenhuma classe fora da banda." saía para 4 de 10
    // clientes do escritório 83 com `status_estrategico='Desbalanceado'`,
    // e com a tabela do desvio logo acima mostrando o contrário.
    VAZIO_DENTRO_DA_BANDA: 'Nenhuma classe fora da banda.',
    VAZIO_SEM_EXCESSO_DE_TETO:
      'Nenhuma classe acima do teto ANBIMA, por isso não há opção de ajuste aqui. '
      + 'A carteira segue fora da banda estratégica: o ajuste contra a banda é decisão sua.',
    NOTA: 'Marcar não escreve nada.',
  }),
  REVISOES_ANTERIORES: Object.freeze({
    TITULO: 'Revisões anteriores',
    VAZIO: 'Primeira revisão registrada para este cliente.',
    // [SHOULD R-4] TRÊS casos, e colapsá-los foi o defeito: o backend lia
    // uma coluna `corpo` que `run_objetos` não tem, então `opcoes_propostas`
    // era `0` em QUALQUER cenário — "0 opções propostas" com a revisão
    // anterior íntegra no S3. Hoje `null` = não registrado (revisão gravada
    // antes do índice existir) e `0` = zero MEDIDO; dizer "0" pelo primeiro
    // é a mesma afirmação falsa com outra causa.
    NAO_REGISTRADO: (data) => `Última em ${data}: opções propostas não registradas.`,
    NENHUMA: (data) => `Última em ${data}: nenhuma opção proposta.`,
    // A US-8 pede a data da última E QUAIS opções foram propostas.
    COM_OPCOES: (data, quantidade, origens) =>
      `Última em ${data}: ${quantidade} `
      + `${quantidade === 1 ? 'opção proposta' : 'opções propostas'}`
      + `${origens ? `: ${origens}` : ''}.`,
  }),
  MEMORANDO: Object.freeze({
    TITULO: 'Memorando de compliance',
    // [SHOULD D-2, design 20/09/2026] `impacto_fiscal` é CONTRATO fechado do
    // backend (`test_impacto_fiscal_e_sempre_nao_apurado`), então a chave não
    // muda lá — traduz-se aqui, exatamente como `MOTIVO_INDISPONIVEL` já faz
    // logo acima "para não ecoar a chave crua". O cartão escrevia
    // "Impacto fiscal: nao_apurado." — snake_case e sem acento, no rodapé do
    // memorando de compliance, visto em duas medições de QA independentes.
    IMPACTO_FISCAL: (rotulo) => `Impacto fiscal: ${rotulo}.`,
    IMPACTO_FISCAL_ROTULO: Object.freeze({
      nao_apurado: 'não apurado',
    }),
    // Chave nova do backend nunca vaza para a tela: cai no MESMO fato que o
    // enum de hoje só sabe dizer. Mesma régua do fallback de
    // `motivoTexto`/`motivoDoDesvioTexto`.
    IMPACTO_FISCAL_PADRAO: 'não apurado',
  }),
  // [SHOULD D-3] A cor do selo ANBIMA vem do DADO. Era `var(--danger)` fixo,
  // então "Dentro do Risco" (conforme) saía em VERMELHO, igualzinho a "Acima
  // do Risco" — cor que não varia não informa, só alarma. Valores = o enum
  // fechado de `api.yaml#Desvio.status_anbima`; nomes = tokens que EXISTEM
  // (`$theme-colors`, `_vars.sass:50`), conferidos um a um.
  STATUS_ANBIMA_COR: Object.freeze({
    'Acima do Risco': 'danger',
    'Próximo do Risco': 'warning',
    'Dentro do Risco': 'success',
    'Não Configurado': 'muted',
  }),
  STATUS_ANBIMA_COR_PADRAO: 'muted',
})


/**
 * O catálogo de atalhos de negócio não carregou — spec-001
 * `project-home-ia-skills/preparacao-reuniao`, US-1 AC4 (G-R2-4 do QA it.2).
 *
 * ⚠️ **A frase tem de ser distinguível de "este escritório não tem
 * atalhos".** O QA abortou o `GET /atalhos` no browser e mediu: a seção
 * sumia inteira, sem mensagem nenhuma — e quem olha conclui que o escritório
 * não contratou atalho nenhum. É o mesmo modo de falha que o `/home-ia` já
 * registrou duas vezes (endpoint 404 exibindo mock como dado real; números
 * do catálogo passando por número do escritório): a metade que FALHOU tem de
 * aparecer, porque ausência silenciosa é pior que erro — o usuário não tem
 * como saber que perguntou.
 */
export const ATALHOS_DE_NEGOCIO = Object.freeze({
  ERRO_AO_CARREGAR: 'Não foi possível carregar seus atalhos.',
  TENTAR_DE_NOVO: 'Tentar de novo',
})

/**
 * A conversa reancorou no cliente que o consultor NOMEOU — spec-001
 * `project-home-ia-skills/preparacao-reuniao`, G-R2-9 do QA it.2.
 *
 * ⚠️ **A troca não pode ser silenciosa.** O chip é âncora implícita; o nome
 * digitado é escolha explícita, e explícito vence — mas o consultor não pode
 * descobrir depois que o foco mudou. Sem o aviso, trocaríamos "duas verdades
 * caladas" por "uma verdade trocada em silêncio", que não é melhor.
 */
export const CLIENTE_REANCORADO = (nome) =>
  nome
    ? 'Cliente em foco agora é ' + nome + '. Foi quem você nomeou no comando.'
    : 'O cliente em foco desta conversa mudou para quem você nomeou no comando.'
